import React, { useState } from "react";
import { Box, Chip, Divider, Tooltip, Typography, styled } from "@mui/material";
import type { MSHatData, ConnectorItem } from "../../types";

// Icons exported from Figma
import iconKnowledgeImport from "./assets/icon-knowledge.svg";
import iconPowerskillsImport from "./assets/icon-powerskills.svg";
import iconProskillsImport from "./assets/icon-proskills.svg";
import iconEndorsedImport from "./assets/icon-endorsed.svg";

// SVG imports resolve as strings (Vite) or { src } objects (Next.js)
const toSrc = (imported: string | { src: string }): string =>
  typeof imported === "string" ? imported : imported.src;

const iconKnowledge = toSrc(iconKnowledgeImport as string | { src: string });
const iconPowerskills = toSrc(
  iconPowerskillsImport as string | { src: string },
);
const iconProskills = toSrc(iconProskillsImport as string | { src: string });
const iconEndorsed = toSrc(iconEndorsedImport as string | { src: string });

interface BackLeftPanelProps {
  hat: MSHatData;
  endorsedSkillIds?: string[];
}

const PanelContainer = styled(Box, {
  label: "BackLeftPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(145, 158, 171, 0.3)",
    borderRadius: "3px",
  },
}));

const TitleSection = styled(Box, {
  label: "BackLeftTitleSection",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
}));

const HatName = styled(Typography, {
  label: "BackLeftHatName",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const ProfileHeading = styled(Typography, {
  label: "BackLeftProfileHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  textAlign: "center",
}));

const ContentArea = styled(Box, {
  label: "BackLeftContentArea",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  flex: 1,
  minHeight: 0,
}));

const SectionContainer = styled(Box, {
  label: "BackLeftSection",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

const SectionHeading = styled(Box, {
  label: "BackLeftSectionHeading",
})(() => ({
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
}));

const SectionLabel = styled(Typography, {
  label: "BackLeftSectionLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textTransform: "uppercase",
  width: 309,
}));

const ChipRow = styled(Box, {
  label: "BackLeftChipRow",
})(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 12px",
}));

const SkillChip = styled(Chip, {
  label: "BackLeftSkillChip",
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "chipColor",
})<{ isSelected?: boolean; chipColor?: string }>(
  ({ isSelected, chipColor = "#0351ab" }) => ({
    fontFamily: "Proxima Nova, sans-serif",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "18px",
    color: isSelected ? "#004b50" : "#ffffff",
    backgroundColor: isSelected ? "#ffffff" : chipColor,
    borderRadius: 10,
    height: 32,
    maxWidth: 178,
    cursor: "pointer",
    padding: "4px 6px 4px 4px",
    "&:hover": {
      backgroundColor: isSelected ? "#ffffff" : chipColor,
    },
    "& .MuiChip-label": {
      padding: "0 8px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiChip-deleteIcon": {
      margin: "0 -2px 0 0",
    },
  }),
);

const EndorsedImg = () => (
  <Tooltip
    title="You've been endorsed!"
    placement="top"
    slotProps={{
      tooltip: {
        sx: {
          backgroundColor: "#FFAB00",
          color: "#1c252e",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: 700,
          fontSize: "12px",
          lineHeight: "18px",
          borderRadius: "6px",
          px: "6px",
          py: "3px",
        },
      },
      arrow: {
        sx: { color: "#FFAB00" },
      },
    }}
    arrow
  >
    <img src={iconEndorsed} alt="Endorsed" width={24} height={24} />
  </Tooltip>
);

const DetailCard = styled(Box, {
  label: "BackLeftDetailCard",
})(() => ({
  border: "1px solid #ffffff",
  borderRadius: 16,
  padding: "8px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  height: 126,
  overflow: "hidden",
  backdropFilter: "blur(30px)",
  flexShrink: 0,
}));

const DetailHeader = styled(Box, {
  label: "BackLeftDetailHeader",
})(() => ({
  display: "flex",
  gap: 16,
  alignItems: "center",
  justifyContent: "center",
}));

const DetailIcon = styled("img", {
  label: "BackLeftDetailIcon",
})(() => ({
  width: 32,
  height: 32,
  objectFit: "contain",
  flexShrink: 0,
}));

const DetailTitle = styled(Typography, {
  label: "BackLeftDetailTitle",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  flex: 1,
}));

const DetailDescription = styled(Typography, {
  label: "BackLeftDetailDescription",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
}));

const parseDescription = (desc: unknown): string => {
  if (!desc) return "";
  if (typeof desc === "string") {
    try {
      const parsed = JSON.parse(desc);
      if (Array.isArray(parsed) && parsed[0]?.children) {
        return parsed[0].children
          .map((child: { text?: string }) => child.text || "")
          .join(" ");
      }
    } catch {
      return desc;
    }
    return desc;
  }
  if (Array.isArray(desc) && desc[0]?.children) {
    return desc[0].children
      .map((child: { text?: string }) => child.text || "")
      .join(" ");
  }
  return "";
};

export const BackLeftPanel = ({
  hat,
  endorsedSkillIds = [],
}: BackLeftPanelProps) => {
  const knowledge = hat.msKnowledge || [];
  const powerSkills = hat.msPowerSkills || [];
  const proSkills = hat.msProSkills || [];

  const allItems = [...knowledge, ...powerSkills, ...proSkills];
  const [selectedId, setSelectedId] = useState<string | null>(
    allItems[0]?.id || null,
  );

  const endorsedSet = new Set(endorsedSkillIds);
  const selectedItem =
    allItems.find((item) => item.id === selectedId) || allItems[0] || null;

  const renderChips = (
    items: ConnectorItem[],
    showEndorsed: boolean = false,
    color?: string,
  ) => (
    <ChipRow>
      {items.map((item) => {
        const isEndorsed = showEndorsed && endorsedSet.has(item.id);
        const isSelected = item.id === selectedId;
        return (
          <SkillChip
            key={item.id}
            label={item.name}
            size="small"
            isSelected={isSelected}
            chipColor={color}
            onClick={() => setSelectedId(item.id)}
            {...(isEndorsed
              ? {
                  deleteIcon: <EndorsedImg />,
                  onDelete: () => setSelectedId(item.id),
                }
              : {})}
          />
        );
      })}
    </ChipRow>
  );

  return (
    <PanelContainer>
      <TitleSection>
        <HatName>{hat.displayName}</HatName>
      </TitleSection>

      <Divider sx={{ borderColor: "rgba(145, 158, 171, 0.2)" }} />

      <ProfileHeading>Top Knowledge &amp; Skills</ProfileHeading>

      <ContentArea>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
          {knowledge.length > 0 && (
            <SectionContainer>
              <SectionHeading>
                <img src={iconKnowledge} alt="" width={24} height={24} />
                <SectionLabel>Knowledge</SectionLabel>
              </SectionHeading>
              {renderChips(knowledge)}
            </SectionContainer>
          )}

          {powerSkills.length > 0 && (
            <SectionContainer>
              <SectionHeading>
                <img src={iconPowerskills} alt="" width={24} height={24} />
                <SectionLabel>Powerskills</SectionLabel>
              </SectionHeading>
              {renderChips(powerSkills, true)}
            </SectionContainer>
          )}

          {proSkills.length > 0 && (
            <SectionContainer>
              <SectionHeading>
                <img src={iconProskills} alt="" width={24} height={24} />
                <SectionLabel>Proskills</SectionLabel>
              </SectionHeading>
              {renderChips(proSkills)}
            </SectionContainer>
          )}
        </Box>

        {selectedItem && (
          <DetailCard>
            <DetailHeader>
              {selectedItem.icon?.url ? (
                <DetailIcon src={selectedItem.icon.url} alt="" />
              ) : selectedItem.category?.icon?.url ? (
                <DetailIcon src={selectedItem.category.icon.url} alt="" />
              ) : null}
              <DetailTitle>{selectedItem.name}</DetailTitle>
            </DetailHeader>
            <DetailDescription>
              {parseDescription(selectedItem.description)}
            </DetailDescription>
          </DetailCard>
        )}
      </ContentArea>
    </PanelContainer>
  );
};
